import React from 'react';
import styled from 'styled-components';
import {useHistory} from 'react-router-dom';
import SGBody from 'components/Typography/SGBody';
import {getSwitchConnectivityParamsPartialUrlFromSearchItem} from '../SiteIDSearch';
import SiteIcon from './SiteIcon';
import Button from '@material-ui/core/Button';
import {Site} from 'hooks/useSiteInfo/interfaces/Site';
import SiteType from 'hooks/useSiteInfo/interfaces/SiteType';
import BusinessStatus from 'hooks/useSiteInfo/interfaces/BusinessStatus';

const StyledContainer = styled.div`
  background-color: ${props => props.theme.cardBg};
  border-bottom-left-radius: 10px;
  border-bottom-right-radius: 10px;
  box-shadow: 0px 2px 1px -1px rgba(255, 255, 255, 0.2), 0px 1px 1px 0px rgba(255, 255, 255, 0.14),
    0px 1px 3px 0px rgba(255, 255, 255, 0.12);
`;

const StyledSearchSuggestion = styled.ul`
  padding-left: 0px;
  margin: 0px;
  max-height: 70vh;
  width: 382px;
  overflow-y: scroll;
`;

const StyledSearchItems = styled.li`
  color: ${props => props.theme.altText};
  padding: 0px;
  list-style: none;
  margin-bottom: 0px;
  overflow: hidden;
`;

const StyledSGSiteName = styled(SGBody)`
  grid-area: siteName;
`;

const StyledSubInfo = styled.div`
  display: flex;
  grid-area: siteInfo;
`;

const StyledSGInfo = styled(SGBody)`
  margin-right: 6px;
`;

const StyledNavLink = styled(Button)`
  border-radius: 0;
  width: 100%;
  text-transform: none;
  justify-content: flex-start;

  .MuiButton-label {
    display: grid;
    font-size: 0.8rem;
    color: ${props => props.theme.subtleText};
    text-decoration: none;
    padding: 8px 16px;
    grid-column-gap: 16px;
    grid-template-areas:
      'icon siteName'
      'icon siteInfo';
    grid-template-columns: 24px auto;
    grid-template-rows: auto auto;
    text-align: left;
    transition-duration: 150ms;
  }

  &:visited .MuiButton-label {
    color: ${props => props.theme.subtleText};
    text-decoration: none;
  }
  &:hover,
  &:focus {
    background-color: ${props => props.theme.tableRow};
  }
  &:hover .MuiButton-label,
  &:focus .MuiButton-label {
    color: ${props => props.theme.altText};
    text-decoration: none;
  }

  &.Closed {
    pointer-events: none;
    color: ${props => props.theme.disabledStateText}66;
    .MuiButton-label {
      color: ${props => props.theme.disabledStateText}66;
    }
    ${StyledSGSiteName} {
      color: ${props => props.theme.disabledStateText}66;
    }
    ${StyledSGInfo} {
      color: ${props => props.theme.disabledStateText}66;
    }
  }
`;

const getSiteTypeLabel = (siteType: SiteType) => {
  switch (siteType) {
    case SiteType.DataCenter:
      return 'Data Center';
    case SiteType.Distribution:
      return 'Distribution Center';
    default:
      return siteType;
  }
};

interface SiteIdSearchSuggestionsProps {
  suggestedSearchItems: Site[];
  urlType: string;
  clearUserInput: () => void;
  setSwitchError: (error: string) => void;
  clickHandler?: (site: Site) => void;
}

function SiteIdSearchSuggestions({
  suggestedSearchItems,
  urlType,
  clearUserInput,
  setSwitchError,
  clickHandler,
}: SiteIdSearchSuggestionsProps) {
  const numberOfSitesToDisplay = 30;
  let history = useHistory();

  const handleItemClick = (site: Site) => {
    if (site.siteType === SiteType.Retail && site.businessStatus === BusinessStatus.Closed) {
      return;
    }

    if (clickHandler) {
      clickHandler(site);
    } else {
      const switchPartialUrl = getSwitchConnectivityParamsPartialUrlFromSearchItem(site);
      const toUrl: string = `/site-search/${urlType}${switchPartialUrl}`;
      history.push(toUrl);
    }
    clearUserInput();
  };

  return (
    <StyledContainer>
      <StyledSearchSuggestion>
        {suggestedSearchItems.slice(0, numberOfSitesToDisplay).map(searchItem => {
          return (
            <StyledSearchItems key={searchItem.siteId}>
              <StyledNavLink
                id={searchItem.siteId}
                onClick={() => handleItemClick(searchItem)}
                className={`
                  ${searchItem.siteType === SiteType.Retail ? searchItem.businessStatus : ''}
              `}
              >
                <SiteIcon siteType={searchItem.siteType} />
                <StyledSGSiteName variant={'b6'}>
                  {searchItem.siteCode} {searchItem.siteName}
                </StyledSGSiteName>
                <StyledSubInfo>
                  {searchItem.brand && (
                    <StyledSGInfo variant={'b6'} className={'subtle'}>
                      {searchItem.brand}
                    </StyledSGInfo>
                  )}
                  {searchItem.geo && (
                    <StyledSGInfo variant={'b6'} className={'subtle'}>
                      {searchItem.geo}
                    </StyledSGInfo>
                  )}
                  {searchItem.siteType && (
                    <StyledSGInfo variant={'b6'} className={'subtle'}>
                      {getSiteTypeLabel(searchItem.siteType)}
                    </StyledSGInfo>
                  )}
                  {searchItem?.address?.city && (
                    <StyledSGInfo variant={'b6'} className={'subtle'}>
                      {searchItem.address.city}
                    </StyledSGInfo>
                  )}
                  {searchItem.siteType === SiteType.Retail && (
                    <StyledSGInfo variant={'b6'} className={'subtle'}>
                      {searchItem.businessStatus}
                    </StyledSGInfo>
                  )}
                </StyledSubInfo>
              </StyledNavLink>
            </StyledSearchItems>
          );
        })}
      </StyledSearchSuggestion>
    </StyledContainer>
  );
}

export default SiteIdSearchSuggestions;
